import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTermsAcceptance } from '@/features/auth/hooks';
import { useIncorporadora } from '@/hooks/useIncorporadora';
import { useAuth } from '@/contexts/AuthContext.hook';
import { LumaSpin } from '@/components/ui/luma-spin';
import { TermsModal } from '@/components/terms/TermsModal';

interface Props {
  children: React.ReactNode;
}

/**
 * Gate para incorporadores: redireciona para /incorporador/onboarding
 * se os termos nao foram aceitos ou a incorporadora nao foi cadastrada.
 *
 * Incorporadores JA CADASTRADOS que precisam aceitar nova versao de termos
 * recebem o TermsModal (popup), igual as demais roles, sem passar pelo
 * onboarding completo novamente.
 *
 * Incorporadores NOVOS (sem incorporadora) sao redirecionados para o
 * onboarding unificado em tela cheia.
 */
export const IncorporadorOnboardingGate: React.FC<Props> = ({ children }) => {
  const { hasAccepted, checking, accepting, acceptTerms, needsAcceptance, currentDocument } = useTermsAcceptance();
  const { incorporadora, isLoading } = useIncorporadora();
  const { loading: authLoading } = useAuth();
  const location = useLocation();

  // Aguarda auth, profile e incorporadora carregarem antes de decidir o fluxo.
  // authLoading cobre o caso em que profile ainda não chegou — sem ele, o hook
  // retorna incorporadora=null imediatamente e o gate redireciona por engano.
  if (authLoading || checking || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LumaSpin size="lg" />
      </div>
    );
  }

  // Se esta na pagina de onboarding, deixa passar (evita loop)
  if (location.pathname === '/incorporador/onboarding') {
    return <>{children}</>;
  }

  // Incorporador JA CADASTRADO mas com termos desatualizados:
  // mostra popup de termos igual as demais roles, sem redirecionar para onboarding.
  if (!hasAccepted && incorporadora) {
    return (
      <>
        {children}
        <TermsModal
          open={needsAcceptance}
          accepting={accepting}
          onAccept={acceptTerms}
          currentDocument={currentDocument}
        />
      </>
    );
  }

  // Incorporador NOVO (sem incorporadora cadastrada): redireciona para onboarding completo.
  if (!incorporadora) {
    return <Navigate to="/incorporador/onboarding" replace />;
  }

  // Tudo completo, renderiza normalmente
  return <>{children}</>;
};
